import { Component, EventEmitter, Inject, Input, OnInit, Optional, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserService } from 'app/services/user.service';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import 'moment/locale/de';
import { subYears } from 'date-fns';
import { RentalService } from 'app/modules/admin/services/rental.service';
import _ from 'lodash';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { TranslocoService } from '@ngneat/transloco';
import {IbanService} from "../../../services/iban.service";

@Component({
    selector: 'app-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.scss'],
})
export class UserProfileComponent implements OnInit {
    // Inline-Verwendung (ohne Dialog): User als Input, Ergebnis als Output
    @Input() inlineUser: any;
    @Output() saved = new EventEmitter<boolean>();

    user: any;
    userForm: UntypedFormGroup;
    maxBirthDate = subYears(new Date(), 16);
    showDeleteButton: boolean = false;
    disableClose: boolean = false;
    countryValues: [] = [];
    hasMissingInformation: boolean = false;
    hasCustomBillingEmail: boolean = false;
    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
        @Optional() public dialogRef: MatDialogRef<UserProfileComponent>,
        private _formBuilder: UntypedFormBuilder,
        private userService: UserService,
        private rentalService: RentalService,
        private _adapter: DateAdapter<any>,
        private translocoService: TranslocoService,
        private ibanService: IbanService,
        public _fuseConfirmationService: FuseConfirmationService,
        @Inject(MAT_DATE_LOCALE) private _locale: string,
    ) {
        this._locale = 'de';
        this._adapter.setLocale(this._locale);
        if (data?.user) {
            this.user = data.user;
            this.showDeleteButton = data.showDeleteButton;
            this.disableClose = data.disableClose;
        }
    }

    ngOnInit(): void {
        if (!this.user && this.inlineUser) {
            this.user = this.inlineUser;
        }
        this.hasCustomBillingEmail = !!this.user?.billingEmail;
        this.userForm = this._formBuilder.group({
            firstName: [
                this.user?.firstName,
                [
                    Validators.required,
                ],
            ],
            lastName: [
                this.user?.lastName,
                [
                    Validators.required,
                ],
            ],
            email: [
                { value: this.user?.email, disabled: true },
                Validators.required,
            ],
            useCustomBillingEmail: [this.hasCustomBillingEmail],
            billingEmail: [
                {
                    value: this.user?.billingEmail,
                    disabled: !this.hasCustomBillingEmail,
                },
                [Validators.required, Validators.email],
            ],
            phoneNumber: [
                this.user?.phoneNumber,
                [
                    Validators.required,
                    Validators.pattern('[- +()0-9]+'),
                    Validators.minLength(7),
                    Validators.maxLength(32),
                ],
            ],
            dateOfBirth: [this.user?.dateOfBirth, Validators.required],
            company: [this.user?.company],
            address: this._formBuilder.group({
                street: [
                    this.user?.address?.street,
                    [
                        Validators.required,
                    ],
                ],
                houseNumber: [
                    this.user?.address?.houseNumber,
                    Validators.required,
                ],
                city: [
                    this.user?.address?.city,
                    [
                        Validators.required,
                    ],
                ],
                postalCode: [
                    this.user?.address?.postalCode,
                    Validators.required,
                ],
                country: [
                    this.user?.address?.country,
                    [
                        Validators.required,
                    ],
                ],
            }),
        });

        const currentLang = this.translocoService.getActiveLang();
        this.setLanguages(currentLang);
        this.translocoService.langChanges$.subscribe({
            next: (lang) =>{
                this.setLanguages(lang);
            }
        });
    }

    setLanguages(lang: string): void{
        this.ibanService.getCountryCodes(lang).subscribe((data) => {
            this.countryValues = data.values;
        });
    }

    onBillingEmailToggle(checked: boolean): void {
        this.hasCustomBillingEmail = checked;
        const control = this.userForm.get('billingEmail');
        if (checked) {
            control.enable();
        } else {
            control.setValue(null);
            control.disable();
        }
    }

    save(): void {
        const { useCustomBillingEmail, ...payload } =
            this.userForm.getRawValue();
        payload.billingEmail = useCustomBillingEmail
            ? payload.billingEmail?.trim()
            : null;
        this.userService
            .update(this.user.sub, payload)
            .subscribe((res) => {
                this.dialogRef?.close(true);
                this.saved.emit(true);
            });
    }

    deleteAccount(): void {
        this.rentalService.getAllForUser().subscribe((data: []) => {
            let stats = null;
            if (data?.length > 0) {
                stats = _.groupBy(data, 'status');
            }

            if (
                stats &&
                (stats?.PAID?.length > 0 ||
                    stats.WAITING_APPROVAL?.length > 0 ||
                    stats.REJECTED?.length > 0)
            ) {
                const dialogRef = this._fuseConfirmationService.open({
                    title: this.translocoService.translate(
                        'user-profile-delete-account-message-active-rentals',
                    ),
                    message: this.translocoService.translate(
                        'user-profile-delete-account-cancel-booking-message',
                    ),
                    actions: {
                        confirm: {
                            show: true,
                            label: 'Ok',
                        },
                        cancel: {
                            show: false,
                        },
                    },
                });
                dialogRef.afterClosed().subscribe(() => {
                    this.dialogRef?.close();
                });
            } else {
                const dialogRef = this._fuseConfirmationService.open({
                    title: this.translocoService.translate(
                        'user-profile-confirm-delete-title',
                    ),
                    message: this.translocoService.translate(
                        'user-profile-confirm-delete-message',
                    ),
                    actions: {
                        confirm: {
                            show: true,
                            label: this.translocoService.translate(
                                'user-profile-delete-button',
                            ),
                        },
                        cancel: {
                            show: true,
                            label: this.translocoService.translate(
                                'user-profile-cancel-button',
                            ),
                        },
                    },
                });
                dialogRef.afterClosed().subscribe((result) => {
                    if (result === 'confirmed') {
                        //delete
                        this.userService.delete(this.user.sub).subscribe(() => {
                            this.userService.logout();
                        });
                    }

                    this.dialogRef?.close();
                });
            }
        });
    }

    onNoClick(): void {
        this.dialogRef?.close();
    }
}
